/**
 * Trackit-style page-relative playhead helpers.
 *
 * Viewport left edge remains `viewportStart` (EEGViewer `currentTime`).
 * The playhead sits at `viewportStart + offsetSeconds` and can be dragged
 * anywhere on the current page without moving the page until seek/playback
 * advances the viewport under a fixed reading-line offset.
 */

export const PAGE_PLAYHEAD_HIT_SLOP_PX = 8;
/** Gap between the reading line and the time chip. */
export const PAGE_PLAYHEAD_CHIP_GAP_PX = 4;
/** Tall enough for bold 11px type; may hang slightly below the 12px ruler. */
export const PAGE_PLAYHEAD_CHIP_HEIGHT_PX = 16;

export type PagePlayheadTimeChipPlacement = "ruler" | "beside-line";

export type PagePlayheadTimeChipLayout = {
  visible: boolean;
  x: number;
  y: number;
  placement: PagePlayheadTimeChipPlacement;
};

/**
 * Place the playhead time chip in the timescale ruler when one exists so it
 * never covers montage names. With no ruler, sit it to the right of the line
 * and hide it while it would overlap the label column.
 */
export function resolvePagePlayheadTimeChipLayout(params: {
  playheadX: number;
  canvasWidth: number;
  chipWidth: number;
  chipHeight: number;
  labelColumnWidth: number;
  waveformTop: number;
  ruler: { top: number; height: number } | null;
}): PagePlayheadTimeChipLayout {
  const canvasWidth = Number.isFinite(params.canvasWidth)
    ? Math.max(0, params.canvasWidth)
    : 0;
  const chipWidth = Number.isFinite(params.chipWidth)
    ? Math.max(0, params.chipWidth)
    : 0;
  const chipHeight = Number.isFinite(params.chipHeight)
    ? Math.max(0, params.chipHeight)
    : 0;
  const playheadX = Number.isFinite(params.playheadX) ? params.playheadX : 0;
  const preferredX = playheadX + PAGE_PLAYHEAD_CHIP_GAP_PX;
  const maxX = Math.max(0, canvasWidth - chipWidth);
  const x = Math.max(0, Math.min(maxX, preferredX));
  const hidden: PagePlayheadTimeChipLayout = {
    visible: false,
    x,
    y: 0,
    placement: "beside-line",
  };
  if (canvasWidth <= 0 || chipWidth <= 0 || chipHeight <= 0) {
    return hidden;
  }

  if (params.ruler) {
    const y =
      params.ruler.height >= chipHeight
        ? params.ruler.top + Math.max(0, (params.ruler.height - chipHeight) / 2)
        : params.ruler.top;
    return { visible: true, x, y, placement: "ruler" };
  }

  const y =
    (Number.isFinite(params.waveformTop) ? params.waveformTop : 0) +
    PAGE_PLAYHEAD_CHIP_GAP_PX;
  const labelColumnWidth = Number.isFinite(params.labelColumnWidth)
    ? Math.max(0, params.labelColumnWidth)
    : 0;
  const overlapsLabels = labelColumnWidth > 0 && x < labelColumnWidth;
  return {
    visible: !overlapsLabels,
    x,
    y,
    placement: "beside-line",
  };
}

export function clampPagePlayheadOffsetSeconds(
  offsetSeconds: number,
  timeScaleSeconds: number,
): number {
  const scale = Number.isFinite(timeScaleSeconds)
    ? Math.max(0, timeScaleSeconds)
    : 0;
  if (!Number.isFinite(offsetSeconds) || scale <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(scale, offsetSeconds));
}

export function pagePlayheadOffsetToCanvasX(params: {
  offsetSeconds: number;
  timeScaleSeconds: number;
  canvasWidth: number;
  plotLeft?: number;
}): number {
  const { canvasWidth, timeScaleSeconds } = params;
  if (
    !Number.isFinite(canvasWidth) ||
    canvasWidth <= 0 ||
    !Number.isFinite(timeScaleSeconds) ||
    timeScaleSeconds <= 0
  ) {
    return 0;
  }
  const plotLeft = Number.isFinite(params.plotLeft)
    ? Math.max(0, params.plotLeft as number)
    : 0;
  const plotWidth = Math.max(1, canvasWidth - plotLeft);
  const offset = clampPagePlayheadOffsetSeconds(
    params.offsetSeconds,
    timeScaleSeconds,
  );
  return plotLeft + (offset / timeScaleSeconds) * plotWidth;
}

export function canvasXToPagePlayheadOffsetSeconds(params: {
  canvasX: number;
  timeScaleSeconds: number;
  canvasWidth: number;
  plotLeft?: number;
}): number {
  const { canvasWidth, timeScaleSeconds } = params;
  if (
    !Number.isFinite(canvasWidth) ||
    canvasWidth <= 0 ||
    !Number.isFinite(timeScaleSeconds) ||
    timeScaleSeconds <= 0
  ) {
    return 0;
  }
  const plotLeft = Number.isFinite(params.plotLeft)
    ? Math.max(0, params.plotLeft as number)
    : 0;
  const plotWidth = Math.max(1, canvasWidth - plotLeft);
  const x = Math.max(plotLeft, Math.min(canvasWidth, params.canvasX));
  return clampPagePlayheadOffsetSeconds(
    ((x - plotLeft) / plotWidth) * timeScaleSeconds,
    timeScaleSeconds,
  );
}

export function resolvePagePlayheadAbsoluteTime(params: {
  viewportStartSeconds: number;
  offsetSeconds: number;
  timeScaleSeconds: number;
}): number {
  const viewportStart = Number.isFinite(params.viewportStartSeconds)
    ? Math.max(0, params.viewportStartSeconds)
    : 0;
  const offset = clampPagePlayheadOffsetSeconds(
    params.offsetSeconds,
    params.timeScaleSeconds,
  );
  return viewportStart + offset;
}

/** Seek so the playhead lands on absoluteTime while keeping the page offset. */
export function viewportStartForPlayheadAbsoluteTime(params: {
  absoluteTimeSeconds: number;
  offsetSeconds: number;
  timeScaleSeconds: number;
  maxViewportStartSeconds?: number;
}): number {
  const absolute = Number.isFinite(params.absoluteTimeSeconds)
    ? Math.max(0, params.absoluteTimeSeconds)
    : 0;
  const offset = clampPagePlayheadOffsetSeconds(
    params.offsetSeconds,
    params.timeScaleSeconds,
  );
  const raw = Math.max(0, absolute - offset);
  if (
    typeof params.maxViewportStartSeconds === "number" &&
    Number.isFinite(params.maxViewportStartSeconds)
  ) {
    return Math.min(Math.max(0, params.maxViewportStartSeconds), raw);
  }
  return raw;
}

export function isPointerNearPagePlayhead(params: {
  canvasX: number;
  offsetSeconds: number;
  timeScaleSeconds: number;
  canvasWidth: number;
  plotLeft?: number;
  slopPx?: number;
}): boolean {
  const playheadX = pagePlayheadOffsetToCanvasX(params);
  const slop = params.slopPx ?? PAGE_PLAYHEAD_HIT_SLOP_PX;
  return Math.abs(params.canvasX - playheadX) <= slop;
}
